// VoiceSpectrum.Web.T2 · components/security/SupportAccessCard.tsx · v12.10.9.3 (#1329 Support access) · 2026-10-07 · VoiceSpectrum AI
// Settings › Security › Platform › Support access (UI-PROTO-T39 v1.0 "support"): whether VoiceSpectrum Support can enter
// this practice, how much (Configuration only / Configuration + Read & write) and for how long (1 hour, 1 day, 1 week,
// 2 weeks, Indefinite; On demand is FUTURE). Starts Granted · Configuration + Read & write · Indefinite. Every change
// asks "Confirm it's you" (withConfirm) and is audited; blocking (or lowering the level) ends open support visits.
// Recent support visits are listed with how many changes they made. A VoiceSpectrum Support session sees it read-only.
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isConfirmCancelled, withConfirm } from './ConfirmItsYou';
import { Ic, InlineError, Seg, fmtClock, fmtDate } from './ui';
import { securityApi, securityErrorMessage, type SupportAccessView, type SupportDuration, type SupportLevel } from '../../lib/securityApi';
import { toast } from '../../lib/toast';

export const SUPPORT_KEY = ['t2-sec-support-access'];

const DUR_LABEL: Record<SupportDuration, string> = { '1h': '1 hour', '1d': '1 day', '1w': '1 week', '2w': '2 weeks', indefinite: 'Indefinite' };
const when = (iso?: string | null) => (iso ? `${fmtDate(iso)}, ${fmtClock(iso)}` : '');

// v12.10.9.4 (#1335 F7): the card joins the page's save model - a change marks it Unsaved and lights the page's Save section /
// Save all changes / the sticky bar; it never saves on its own hidden button. Block / Grant access again stay one-click (+ Confirm).
export interface SupportCardState { dirty: boolean; save: () => Promise<void>; discard: () => void; saving: boolean }

export default function SupportAccessCard({ orgName, onStateChange }: { orgName: string; onStateChange?: (s: SupportCardState) => void }) {
  const qc = useQueryClient();
  const q = useQuery<SupportAccessView>({ queryKey: SUPPORT_KEY, queryFn: () => securityApi.getSupportAccess(), retry: false });
  const [level, setLevel] = useState<SupportLevel>('config_rw');
  const [duration, setDuration] = useState<SupportDuration>('indefinite');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const a = q.data?.access;
  useEffect(() => { if (a) { setLevel(a.level); setDuration(a.duration); } }, [a]);
  // #1335: unsaved = the choices differ from what is saved (Granted or Blocked - saving while Blocked grants with these choices).
  const changed = !!a && !!q.data?.canChange && (level !== a.level || duration !== a.duration);
  const saveRef = useRef<() => Promise<void>>(async () => {});
  const reportRef = useRef(onStateChange); reportRef.current = onStateChange;
  useEffect(() => {
    reportRef.current?.({ dirty: changed, saving: busy, save: () => saveRef.current(), discard: () => { if (a) { setLevel(a.level); setDuration(a.duration); } } });
  }, [changed, busy, a]);

  if (q.isError) return null;   // no access to Security › Platform - the page says so already
  if (!a || !q.data) return <section className="settings-section-card"><div className="sec-body">Loading Support access…</div></section>;

  const granted = a.state === 'granted';
  const canChange = q.data.canChange;

  saveRef.current = () => save('granted');
  async function save(state: 'granted' | 'blocked') {
    setBusy(true); setErr(null);
    try {
      const r = await withConfirm(() => securityApi.putSupportAccess({ state, level, duration }), 'Changing VoiceSpectrum Support’s access');
      await qc.invalidateQueries({ queryKey: SUPPORT_KEY });
      toast('success', state === 'blocked'
        ? `VoiceSpectrum Support is blocked${r.endedSessions ? ` - ${r.endedSessions} open visit ended` : ''}.`
        : 'Support access saved.');
    } catch (e) {
      if (!isConfirmCancelled(e)) setErr(securityErrorMessage(e));
      throw e;
    } finally { setBusy(false); }
  }

  const untilText = granted
    ? (a.until ? `Until ${when(a.until)} (${DUR_LABEL[a.duration]})` : 'No end date')
    : (a.endedAt ? `Ended ${when(a.endedAt)}${a.duration !== 'indefinite' ? ` (${DUR_LABEL[a.duration]})` : ''}` : 'Blocked');

  return (
    <section className={`settings-section-card${changed ? ' dirty' : ''}`} id="sec-support" data-testid="support-access-card">
      <header className="settings-section-header">
        <div className="settings-section-headrow">
          <h2>Support access</h2>
          <div className="settings-actions">
            {canChange && granted && <button type="button" className="settings-secondary" disabled={busy} onClick={() => void save('blocked').catch(() => {})}>Block VoiceSpectrum Support</button>}
            {canChange && !granted && !changed && <button type="button" className="settings-secondary" disabled={busy} onClick={() => void save('granted').catch(() => {})}>Grant access again</button>}
            {canChange && (
              <>
                <button type="button" className="settings-secondary" disabled={busy || !changed} onClick={() => { setLevel(a.level); setDuration(a.duration); }}>Discard</button>
                <button type="button" className="settings-primary" disabled={busy || !changed} onClick={() => void save('granted').catch(() => {})}>{busy ? 'Saving…' : 'Save section'}</button>
              </>
            )}
          </div>
        </div>
        <p className="sec-desc">Whether VoiceSpectrum Support can enter your workspace, how much they can do, and for how long.</p>
      </header>
      <div className="sec-body">
        {err && <div style={{ marginBottom: 14 }}><InlineError text={err} /></div>}
        {granted
          ? <div className="sc-note good" style={{ marginTop: 0, marginBottom: 14 }}><span className="dot" /><span>VoiceSpectrum Support can enter {orgName} to help you. While they are in, every page shows them a purple banner and everything they do is recorded in the audit log as <b>VoiceSpectrum Support</b>. Changing this asks you to confirm it’s you.</span></div>
          : <div className="sc-warn" style={{ marginTop: 0, marginBottom: 14 }}><Ic k="warn" /><div>VoiceSpectrum Support can’t enter {orgName}. If you call us for help, grant access here first - pick how much and for how long.</div></div>}
        {q.data.isSupport && <div className="sc-lock-note" style={{ marginTop: 0, marginBottom: 14 }}><Ic k="lock" /><span>Only the practice can change this - VoiceSpectrum Support sees it read-only.</span></div>}
        {!q.data.isSupport && !canChange && <div className="sc-lock-note" style={{ marginTop: 0, marginBottom: 14 }}><Ic k="lock" /><span>You can see this but not change it. Ask someone whose group can change Support access.</span></div>}

        <div className="sup-kv">
          <span className="k">Who</span><span><b>VoiceSpectrum Support</b></span>
          <span className="k">Status</span><span><span className={`sup-pill ${granted ? 'good' : 'bad'}`}>● {granted ? 'Granted' : 'Blocked'}</span></span>
          <span className="k">Access</span>
          <span>
            <Seg<SupportLevel> value={level} onChange={setLevel} disabled={!canChange || busy} label="Support access level"
              options={[['config', 'Configuration only'], ['config_rw', 'Configuration + Read & write']]} />
            <div className="sup-help">Configuration only = Settings. Read &amp; write = also messages, contacts, recordings - everything an Owner can do.</div>
          </span>
          <span className="k">For how long</span>
          <span>
            <div className="sup-row">
              <Seg<SupportDuration> value={duration} onChange={setDuration} disabled={!canChange || busy} label="How long Support access lasts"
                options={[['1h', '1 hour'], ['1d', '1 day'], ['1w', '1 week'], ['2w', '2 weeks'], ['indefinite', 'Indefinite']]} />
              <span className="sup-od" title="Not available yet">On demand<span className="sc-future">Future</span></span>
            </div>
            <div className="sup-help">{untilText}{granted && changed && duration !== 'indefinite' ? ` · saving starts a new ${DUR_LABEL[duration]} from now` : ''}</div>
            {!granted && changed && <div className="sup-help"><b>Status: Blocked</b> - saving grants access with these choices.</div>}
          </span>
        </div>

        <h4 className="sup-h">Recent support sessions</h4>
        {q.data.visits.length === 0
          ? <div className="sup-help">VoiceSpectrum Support hasn’t been in yet.</div>
          : (
            <div className="sup-table-wrap">
              <table className="sup-table">
                <thead><tr><th>Started</th><th>Ended</th><th>Reason / ticket</th><th>Changes</th></tr></thead>
                <tbody>
                  {q.data.visits.map((v) => (
                    <tr key={v.started}>
                      <td>{when(v.started)}</td>
                      <td>{v.ended ? fmtClock(v.ended) : 'Still open'}</td>
                      <td>{v.reason ?? '-'}</td>
                      <td>{v.changes === 0 ? 'No changes' : `${v.changes} ${v.changes === 1 ? 'change' : 'changes'} in the audit log`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        <div className="sup-help" style={{ marginTop: 10 }}>The support person’s own name is kept in our records; your audit log shows VoiceSpectrum Support.</div>
      </div>
    </section>
  );
}
